/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Move the help links selectors to the mobile nav/back to the announcement bar if necessary
     * and add additional links to the fixed mobile menu
     */
    moveLinks() {
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');
      
      if (!menuAnnouncementLinks && this.menu) {
        const mobNav = document.createElement('nav');
        mobNav.classList.add('mob__announcement-links');

        const scrollableWrapper = document.createElement('div');
        scrollableWrapper.classList.add('mob-menu-scrollable-wrapper');

        const mobNavUl = document.createElement('ul');
        mobNavUl.classList.add('secondary-nav');
        
        const accountElement = document.createElement('li');
        accountElement.classList.add('mobile-fixed-item');
        
        const customerIcon = document.querySelector('.header__icon--account svg');
        let customerIconHTML = customerIcon ? customerIcon.outerHTML : this.getDefaultCustomerIcon();
        
        const isLoggedIn = document.querySelector('.header__icon--account[href$="/account"]');
        const accountUrl = isLoggedIn ? '/account' : '/account/login';
        const accountText = isLoggedIn ? 'Minha Conta' : 'Iniciar sessão';
        
        accountElement.innerHTML = `
          <a class="secondary-nav__item" href="${accountUrl}">
            ${customerIconHTML}
            <span>${accountText}</span>
          </a>
        `;
        mobNavUl.appendChild(accountElement);
        
        const ctaPopup = document.querySelector('.secondary-nav__cta-popup');
        if (ctaPopup) {
          const ctaElement = document.createElement('li');
          ctaElement.classList.add('mobile-fixed-item');
          
          // Criar accordion em vez de popup
          const accordionContent = ctaPopup.querySelector('.secondary-nav__cta-popup--content').cloneNode(true);
          
          // Criar header do accordion
          const accordionHeader = document.createElement('button');
          accordionHeader.classList.add('mobile-accordion-header', 'secondary-nav__item');
          
          // Usar o mesmo ícone do botão CTA
          const ctaIcon = document.querySelector('.secondary-nav__cta .icon');
          accordionHeader.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M4.125 11.9046V9.40789C4.125 7.64211 4.84933 5.94865 6.13864 4.70005C7.42795 3.45145 9.17664 2.75 11 2.75C12.8234 2.75 14.572 3.45145 15.8614 4.70005C17.1507 5.94865 17.875 7.64211 17.875 9.40789V11.9046M4.125 11.9046C4.125 11.4632 4.30608 11.0398 4.62841 10.7276C4.95074 10.4155 5.38791 10.2401 5.84375 10.2401H6.70312C7.15897 10.2401 7.59614 10.4155 7.91846 10.7276C8.24079 11.0398 8.42188 11.4632 8.42188 11.9046V14.4013C8.42188 14.8428 8.24079 15.2661 7.91846 15.5783C7.59614 15.8904 7.15897 16.0658 6.70312 16.0658H5.84375C5.38791 16.0658 4.95074 15.8904 4.62841 15.5783C4.30608 15.2661 4.125 14.8428 4.125 14.4013V11.9046ZM17.875 11.9046C17.875 11.4632 17.6939 11.0398 17.3716 10.7276C17.0493 10.4155 16.6121 10.2401 16.1562 10.2401H15.2969C14.841 10.2401 14.4039 10.4155 14.0815 10.7276C13.7592 11.0398 13.5781 11.4632 13.5781 11.9046V14.4013C13.5781 14.8428 13.7592 15.2661 14.0815 15.5783C14.4039 15.8904 14.841 16.0658 15.2969 16.0658H16.1562M17.875 11.9046V14.4013C17.875 14.8428 17.6939 15.2661 17.3716 15.5783C17.0493 15.8904 16.6121 16.0658 16.1562 16.0658M16.1562 16.0658C16.1562 17.4448 13.848 18.5625 11 18.5625" stroke="#090302" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Fale com a Orthocrin</span>`;
          
          // Criar container para o accordion
          const accordionContainer = document.createElement('div');
          accordionContainer.classList.add('mobile-accordion');
          
          // Configurar o container do conteúdo do accordion
          accordionContent.classList.remove('secondary-nav__cta-popup--content');
          accordionContent.classList.add('mobile-accordion-content');
          
          // Adicionar header e conteúdo ao container
          accordionContainer.appendChild(accordionHeader);
          accordionContainer.appendChild(accordionContent);
          
          // Adicionar accordion ao item da lista
          ctaElement.appendChild(accordionContainer);
          mobNavUl.appendChild(ctaElement);
          
          // Configurar event listeners para o accordion
          this.setupMobileAccordion(accordionContainer);
        }
        
        const customLinkText = document.querySelector('meta[name="custom_link_text"]')?.getAttribute('content');
        const customLinkHref = document.querySelector('meta[name="custom_link_href"]')?.getAttribute('content');
        
        if (customLinkText && customLinkHref && customLinkText.toLowerCase() !== 'blog') {
          const customLinkElement = document.createElement('li');
          customLinkElement.classList.add('mobile-fixed-item');
          customLinkElement.innerHTML = `
            <a class="secondary-nav__item" href="${customLinkHref}">
              <span>${customLinkText}</span>
            </a>
          `;
          mobNavUl.appendChild(customLinkElement);
        }

        // Adicionar links sociais abaixo dos links principais
        const socialLinks = document.createElement('ul');
        socialLinks.classList.add('social', 'inline-flex', 'flex-wrap', 'mobile-social-links');
        socialLinks.innerHTML = `
          <li>
            <a class="social__link flex items-center justify-center" href="" target="_blank" rel="noopener" title="Tweetar no X">
              <svg fill="currentColor" width="40" height="40" viewBox="1 1 22 22" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></svg>
            </a>
          </li>
          <li>
            <a class="social__link flex items-center justify-center" href="https://www.facebook.com/Orthocrin/" target="_blank" rel="noopener" title="Partilhar no Facebook">
              <svg width="24" height="24" viewBox="0 0 14222 14222" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M14222 7112c0 3549.352-2600.418 6491.344-6000 7024.72V9168h1657l315-2056H8222V5778c0-562 275-1111 1159-1111h897V2917s-814-139-1592-139c-1624 0-2686 984-2686 2767v1567H4194v2056h1806v4968.72C2600.418 13603.344 0 10661.352 0 7112 0 3184.703 3183.703 1 7111 1s7111 3183.703 7111 7111Zm-8222 7025c362 57 733 86 1111 86-377.945 0-749.003-29.485-1111-86.28Zm2222 0v-.28a7107.458 7107.458 0 0 1-167.717 24.267A7407.158 7407.158 0 0 0 8222 14137Zm-167.717 23.987C7745.664 14201.89 7430.797 14223 7111 14223c319.843 0 634.675-21.479 943.283-62.013Z"></path></svg>
            </a>
          </li>
          <li>
            <a class="social__link flex items-center justify-center" href="https://api.whatsapp.com/send?phone=5531998026629" target="_blank" rel="noopener" title="Translation missing: pt-PT.general.social.share_on_whatsapp">
              <svg viewBox="0 0 30.667 30.667" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M30.667 14.939c0 8.25-6.74 14.938-15.056 14.938a15.1 15.1 0 0 1-7.276-1.857L0 30.667l2.717-8.017a14.787 14.787 0 0 1-2.159-7.712C.559 6.688 7.297 0 15.613 0c8.315.002 15.054 6.689 15.054 14.939zM15.61 2.382c-6.979 0-12.656 5.634-12.656 12.56 0 2.748.896 5.292 2.411 7.362l-1.58 4.663 4.862-1.545c2 1.312 4.393 2.076 6.963 2.076 6.979 0 12.658-5.633 12.658-12.559C28.27 8.016 22.59 2.382 15.61 2.382zm7.604 15.998c-.094-.151-.34-.243-.708-.427-.367-.184-2.184-1.069-2.521-1.189-.34-.123-.586-.185-.832.182-.243.367-.951 1.191-1.168 1.437-.215.245-.43.276-.799.095-.369-.186-1.559-.57-2.969-1.817-1.097-.972-1.838-2.169-2.052-2.536-.217-.366-.022-.564.161-.746.165-.165.369-.428.554-.643.185-.213.246-.364.369-.609.121-.245.06-.458-.031-.643-.092-.184-.829-1.984-1.138-2.717-.307-.732-.614-.611-.83-.611-.215 0-.461-.03-.707-.03s-.646.089-.983.456-1.291 1.252-1.291 3.054c0 1.804 1.321 3.543 1.506 3.787.186.243 2.554 4.062 6.305 5.528 3.753 1.465 3.753.976 4.429.914.678-.062 2.184-.885 2.49-1.739.308-.858.308-1.593.215-1.746z"></path></svg>
            </a>
          </li>
          <li>
            <a class="social__link flex items-center justify-center" href="https://www.instagram.com/orthocrin/" target="_blank" rel="noopener" title="Translation missing: pt-PT.general.social.share_on_telegram">
              <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><circle cx="15.238" cy="4.948" r="1.238"></circle><path d="M19.47 3.444A5.11 5.11 0 0 0 16.548.527a7.351 7.351 0 0 0-2.43-.466C13.05.014 12.713 0 9.999 0c-2.712 0-3.057 0-4.12.06A7.351 7.351 0 0 0 3.45.528 5.11 5.11 0 0 0 .528 3.444 7.317 7.317 0 0 0 .06 5.87C.014 6.936 0 7.274 0 9.982s0 3.053.06 4.113c.018.829.176 1.649.468 2.425a5.11 5.11 0 0 0 2.922 2.917 7.35 7.35 0 0 0 2.429.5c1.069.047 1.407.06 4.12.06s3.058 0 4.12-.06a7.351 7.351 0 0 0 2.429-.466 5.11 5.11 0 0 0 2.922-2.918 7.31 7.31 0 0 0 .467-2.424c.047-1.067.06-1.405.06-4.113s0-3.053-.06-4.113a7.317 7.317 0 0 0-.467-2.459zm-1.437 10.537a5.439 5.439 0 0 1-.34 1.843 3.262 3.262 0 0 1-1.87 1.87 5.451 5.451 0 0 1-1.825.34c-1.04.046-1.332.06-3.996.06-2.664 0-2.937 0-3.995-.06a5.451 5.451 0 0 1-1.825-.34 3.255 3.255 0 0 1-1.878-1.87 5.439 5.439 0 0 1-.34-1.823c-.046-1.038-.06-1.33-.06-3.992s0-2.934.06-3.992c.006-.63.121-1.253.34-1.844a3.255 3.255 0 0 1 1.878-1.87 5.451 5.451 0 0 1 1.825-.339c1.038-.046 1.331-.06 3.995-.06s2.937 0 3.996.06c.623.008 1.24.123 1.824.34.86.331 1.54 1.01 1.872 1.87.216.583.331 1.2.34 1.823.046 1.038.06 1.33.06 3.992 0 2.661 0 2.948-.047 3.992h-.014z"></path><path d="M9.991 14.753a4.761 4.761 0 1 1 0-9.523 4.761 4.761 0 0 1 0 9.523zm0-1.905a2.857 2.857 0 1 0 0-5.713 2.857 2.857 0 0 0 0 5.713z"></path></svg>
            </a>
          </li>
        `;
        
        // Adicionar os ícones sociais à navbar
        mobNav.appendChild(mobNavUl);
        // mobNav.appendChild(socialLinks);
        
        this.menu.appendChild(mobNav);
        
        // Criar wrapper scrollável para o conteúdo do menu (apenas se ainda não existir)
        this.createScrollableWrapper();
      }
    }
    
    /**
     * Retorna o ícone padrão para o CTA caso não seja possível capturar do tema
     */
    getDefaultCtaIcon() {
      return '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 10.02V11" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 10.02V11" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 16.99C13.48 16.99 14.68 16.15 15.24 14.95" stroke="currentColor" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round" stroke-linejoin="round"/><path d="M21.8 10.41C21.8 15.57 17.56 19.82 12.4 19.82L13.49 21.97" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M3 13.57C3 8.41 7.24 4.16 12.4 4.16L11.31 2.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    
    /**
     * Retorna o ícone padrão para o usuário caso não seja possível capturar do tema
     */
    getDefaultCustomerIcon() {
      return '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M12 2a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 1.429a3.571 3.571 0 1 0 0 7.142 3.571 3.571 0 0 0 0-7.142zm0 10c2.558 0 5.114.471 7.664 1.411A3.571 3.571 0 0 1 22 18.19v3.096c0 .394-.32.714-.714.714H2.714A.714.714 0 0 1 2 21.286V18.19c0-1.495.933-2.833 2.336-3.35 2.55-.94 5.106-1.411 7.664-1.411zm0 1.428c-2.387 0-4.775.44-7.17 1.324a2.143 2.143 0 0 0-1.401 2.01v2.38H20.57v-2.38c0-.898-.56-1.7-1.401-2.01-2.395-.885-4.783-1.324-7.17-1.324z"/></svg>';
    }
    
    /**
     * Cria wrapper scrollável para o conteúdo do menu mobile
     */
    createScrollableWrapper() {
      if (!this.menu || document.querySelector('.main-menu__scrollable')) return;
      
      const scrollableWrapper = document.createElement('div');
      scrollableWrapper.classList.add('main-menu__scrollable');
      
      // Mover todo conteúdo existente (exceto os links fixos) para o wrapper scrollável
      const elementsToWrap = Array.from(this.menu.children).filter(child => 
        !child.classList.contains('mob__announcement-links')
      );
      
      // Inserir o wrapper scrollável no início do menu
      this.menu.insertBefore(scrollableWrapper, this.menu.firstChild);
      
      // Mover os elementos para o wrapper
      elementsToWrap.forEach(element => {
        scrollableWrapper.appendChild(element);
      });
    }

    /**
     * Configura event listeners para o accordion mobile
     */
    setupMobileAccordion(accordionContainer) {
      if (!accordionContainer) return;

      const accordionHeader = accordionContainer.querySelector('.mobile-accordion-header');
      const accordionContent = accordionContainer.querySelector('.mobile-accordion-content');

      if (!accordionHeader || !accordionContent) return;

      // Inicialmente, o conteúdo do accordion está fechado
      accordionContent.style.display = 'none'; 
      accordionHeader.setAttribute('aria-expanded', 'false');

      // Alternar exibição do accordion ao clicar no header
      accordionHeader.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        
        const isExpanded = accordionHeader.getAttribute('aria-expanded') === 'true';
        
        // Alternar estado
        accordionHeader.setAttribute('aria-expanded', !isExpanded);
        
        // Alternar visibilidade com slide animation
        if (isExpanded) {
          // Fechar accordion
          accordionContent.style.maxHeight = accordionContent.scrollHeight + 'px';
          setTimeout(() => {
            accordionContent.style.maxHeight = '0px';
            setTimeout(() => {
              accordionContent.style.display = 'none';
            }, 300);
          }, 10);
        } else {
          // Abrir accordion
          accordionContent.style.display = 'block';
          accordionContent.style.maxHeight = '0px';
          setTimeout(() => {
            accordionContent.style.maxHeight = accordionContent.scrollHeight + 'px';
          }, 10);
        }
      });

      // Impedir propagação de cliques dentro do conteúdo
      accordionContent.addEventListener('click', function (e) {
        e.stopPropagation();
      });
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
